import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, FormCheck, FormControl, Modal, OverlayTrigger, Popover, ProgressBar, Tooltip } from 'react-bootstrap';
import { BiCheck, BiLoaderAlt, BiMinus, BiX } from 'react-icons/bi';
import { MdOutlineAutoFixHigh } from 'react-icons/md';
import { SketchPicker } from 'react-color';
import { AUTOFILL_STATUS, getWordScoreFrequencies, useSwdContext, WORD_SUGGESTION_STATUS } from '../../libs/autofillLib';
import { PuzzlePreferenceKey, useAutofill, useCharGrid, useClues, useCursor, useFurnishings, usePuzzlePreferences, useUpdateCharGridAtLocs, useWordSuggestor } from './BoardInteractionContext';
import CluesPanel from './CluesPanel';
import WordSuggestorPanel from './WordSuggestorPanel';
import { AiOutlineBgColors } from 'react-icons/ai';
import { furnishingsObjectToList, FurnishingType } from '../../libs/furnishingsLib';
import { FaRegCircle } from 'react-icons/fa';
import { TbCircleDotted } from 'react-icons/tb';
import { useAppContext } from '../../App';
import KeyboardEventHandler from '../../libs/keyboardLib';
import ClueLinkageModal from './ClueLinkageModal';
import { removeClueCommand } from '../../libs/puzzleLib';


export const TAB_KEY = {
  FILL: 'fill',
  CLUES: 'clues',
};

const DEFAULT_COLORS = ['transparent', '#ff5827', '#f5a623', '#f8e71c', '#7ed321', '#00fffb', '#ff51f8', '#a891eb'];




function MinScoreSlider({ currentMinScore, setMinScore, ...props }) {

  // Any time the scored word database is reloaded, do a full reset of this component
  const { swdVersion } = useSwdContext();
  const [freqs, setFreqs] = useState(null);
  useEffect(() => {
    setFreqs(getWordScoreFrequencies());
  }, [swdVersion]);

  // Component state
  const [displayedSliderValue, setDisplayedSliderValue] = useState(currentMinScore);   // this is used to decouple the slider with the actual minScore for debouncing purposes
  useEffect(() => setDisplayedSliderValue(currentMinScore), [currentMinScore]);   // but we DO want currentMinScore changes to propagate to displayedSliderValue
  const sliderTimeout = useRef(null);
  function onSliderChange(newValue) {
    // Set displayed slider value immediately
    setDisplayedSliderValue(newValue);
    // Throttle upward calls to setMinScore by a whole second
    clearTimeout(sliderTimeout.current);
    sliderTimeout.current = setTimeout(() => setMinScore(newValue), 1000);
  }

  if (!freqs) return <div {...props}><div className='fst-italic text-muted small text-center'>Word database not loaded.</div></div>;

  const minValue = Math.min(...freqs.keys());
  const maxValue = Math.max(...freqs.keys());

  // We can divide up the range into rectangles that span from one score value to the next (like a Riemann sum!)
  const bounds = (Array.from(freqs.entries())
    .sort((a, b) => a[0] - b[0])       // sort by increasing score value
    .map(([scoreValue, _], idx, arr) => ({
      cumulativeNumWords: arr.filter(e => e[0] >= scoreValue).reduce((acc, cur) =>  acc + cur[1], 0),
      lowBoundScore: scoreValue,
      highBoundScore: arr[idx+1]?.[0],
    }))
  );

  const VIEWBOX_HEIGHT = 30;
  const TOTAL_NUM_WORDS = Math.max(...bounds.map(o => o.cumulativeNumWords));
  const HEIGHT_SCALE = VIEWBOX_HEIGHT / TOTAL_NUM_WORDS * 0.9;
  const VIEWBOX_WIDTH_PADDING = (maxValue - minValue) * 0.075;

  const rects = bounds.map(({ cumulativeNumWords, lowBoundScore, highBoundScore }) => (
    <rect
      x={lowBoundScore + VIEWBOX_WIDTH_PADDING}
      width={highBoundScore ? highBoundScore - lowBoundScore : VIEWBOX_WIDTH_PADDING}
      y={VIEWBOX_HEIGHT - HEIGHT_SCALE*cumulativeNumWords}
      height={VIEWBOX_HEIGHT}
      fill={lowBoundScore >= displayedSliderValue ? '#42d1fc' : 'lightgrey'}
      key={`minscore-reimann-${lowBoundScore}`}
    />
  )).concat([
    <rect
      x={0}
      width={VIEWBOX_WIDTH_PADDING}
      y={VIEWBOX_HEIGHT - HEIGHT_SCALE*TOTAL_NUM_WORDS}
      height={VIEWBOX_HEIGHT}
      fill={'lightgrey'}
      key='minscore-last-reimann'
    />
  ]);

  return (
    <div {...props}>
      <svg viewBox={`0 0 ${maxValue - minValue + 2*VIEWBOX_WIDTH_PADDING} ${VIEWBOX_HEIGHT}`}>
        {rects}
        <line
          x1={displayedSliderValue + VIEWBOX_WIDTH_PADDING}
          y1={0}
          x2={displayedSliderValue + VIEWBOX_WIDTH_PADDING}
          y2={VIEWBOX_HEIGHT}
          stroke='blue'
          strokeWidth={1}
        />
      </svg>
      <Form.Range
        id='min-word-score-slider'
        min={minValue}
        max={maxValue}
        step={1}
        value={displayedSliderValue}
        onChange={e => onSliderChange(parseInt(e.target.value))}
      />
      <div className='text-muted text-center small fst-italic'>
        {Array.from(freqs.entries()).filter(o => o[0] >= displayedSliderValue).reduce((acc, cur) => acc + cur[1], 0).toLocaleString()} words &ge; {displayedSliderValue}.
      </div>
    </div>
  );
}






const ConstructorSidePanel = React.forwardRef(({ tabKey, setTabKey, pxHeight=400, ...props }, ref) => {

  const [currentColor, setCurrentColor] = useState('#F5A623');
  const [isHoveringOverAutofillToolbar, setIsHoveringOverAutofillToolbar] = useState(false);
  const [clueLinkageModalShowing, setClueLinkageModalShowing] = useState(null);   // if null, modal not showing at all
  const [rebusModalShowing, setRebusModalShowing] = useState(false);
  const [currentRebusValue, setCurrentRebusValue] = useState('');
  const [currentAlternateSolutionsValue, setCurrentAlternateSolutionsValue] = useState('');
  const [allowAnySolution, setAllowAnySolution] = useState(false);
  const [currentBkgdChar, setCurrentBkgdChar] = useState('');

  const { windowWidth } = useAppContext();
  const { getPuzzlePreference, setMinScore } = usePuzzlePreferences();
  const { turnOnContinuousAutofill, turnOffContinuousAutofill, autofillStatus, continuouslyAutofill } = useAutofill();
  const { cursorLoc, currentSlotName } = useCursor();
  const { furnishings, furnishingValueAt, updateColorFurnishing, removeColorFurnishing, toggleCircleFurnishingsAtLocs, updateAlternateSolutions, removeAlternateSolutions, updateBackgroundFurnishing } = useFurnishings();
  const charGrid = useCharGrid();
  const updateCharGridAtLocs = useUpdateCharGridAtLocs();
  const { clues, setCluesAt } = useClues();
  const { wordsMap, isCurrentlyEvaluatingMore } = useWordSuggestor();

  var numWordsWithFill = 0, numWordsWithoutFill = 0, numWordsNotSure = 0;
  if (wordsMap) {
    wordsMap.forEach(value => {
      if (value.status === WORD_SUGGESTION_STATUS.FILLABLE) {
        ++numWordsWithFill;
      } else if (value.status === WORD_SUGGESTION_STATUS.UNFILLABLE) {
        ++numWordsWithoutFill;
      } else if (value.status === WORD_SUGGESTION_STATUS.NOT_EVALUATED || value.status === WORD_SUGGESTION_STATUS.TIMEOUT) {
        ++numWordsNotSure;    // this leaves out the intentionally ignored words (words with too low scores)
      }
    });
  }
  const totalConsideredWords = numWordsWithoutFill + numWordsWithFill + numWordsNotSure;  // leaves out ignored words

  /**
   * Deals with "transparent" color by removing furnishing for the cursor cell, if cursorLoc is defined.
   * Does not do anything if there is already an equivalent color furnishing on the cursor loc.
   */
  function updateFurnishingColorForCursorLoc(newColorHex) {
    if (cursorLoc) {
      if (newColorHex === 'transparent' && furnishingValueAt(cursorLoc, FurnishingType.COLOR)) {
        removeColorFurnishing(cursorLoc);
      } else if (furnishingValueAt(cursorLoc, FurnishingType.COLOR) !== newColorHex) {
        updateColorFurnishing(newColorHex, cursorLoc);
      }
    }
  }


  const rebusModal = (
    <Modal
      show={rebusModalShowing}
      onShow={() => { 
        if (cursorLoc) {
          // Set the value of the input field
          const fill = charGrid[cursorLoc[0]][cursorLoc[1]];
          if (fill.startsWith('r:') || fill.startsWith('o:')) {
            setCurrentRebusValue(fill.slice(2));
          } else if (/^[A-Z]$/.test(fill)) {
            setCurrentRebusValue(fill);
          } else {
            setCurrentRebusValue('');
          }

          // Also set the value of the alternate solutions textarea
          const alts = furnishingValueAt(cursorLoc, FurnishingType.ALTERNATE_SOLUTION);
          if (alts === '$ANY_SOLUTION$') {
            setAllowAnySolution(true);
            setCurrentAlternateSolutionsValue('');
          } else {
            setAllowAnySolution(false);
            setCurrentAlternateSolutionsValue(alts || '');
          }

          // Also set the value of the current loc's background furnishing
          const bkgd = furnishingValueAt(cursorLoc, FurnishingType.BACKGROUND) || '';
          setCurrentBkgdChar(bkgd);
        }
      }}
      onHide={() => {
        // Don't do anything unless they click the submit/save button
        setRebusModalShowing(false);
      }}
    >
      <Modal.Header className='purple-2-bkgd' closeButton><strong>Advanced cell properties</strong></Modal.Header>
      <Modal.Body>
        <div className='mb-1'>Enter a word, character, symbol, emoji, or other string that you want to appear in this grid cell:</div>
        <FormControl value={currentRebusValue} onChange={e => setCurrentRebusValue(e.target.value)} autoFocus />
        
        <div className='mt-4'>If there are any other acceptable solutions to this cell, enter them below (one per line):</div>
        <FormControl as='textarea' rows={4} disabled={allowAnySolution} value={currentAlternateSolutionsValue} onChange={e => setCurrentAlternateSolutionsValue(e.target.value)} onBlur={() => {
          setCurrentAlternateSolutionsValue(alts => alts.toUpperCase().split('\n').map(s => s.trim()).filter((e, idx, lst) => lst.indexOf(e) === idx).join('\n'));  // make them uppercase and unique
        }} />
        <div className='small fst-italic'>Rebus solutions will be accepted in any case (upper or lowercase).</div>
        <FormCheck
          type='checkbox'
          id='accept-any-solution'
          label='Allow any value as a correct solution'
          checked={allowAnySolution}
          onChange={e => setAllowAnySolution(e.target.checked)}
        />

        <div className='d-flex justify-content-center mt-3'>
          <Button
            variant='outline-primary'
            onClick={() => {
              // Convert the input field contents into the actual rebus charGrid entry, and update the alternate solutions furnishing
              if (cursorLoc) {
                const text = currentRebusValue.trim();
                const alts = currentAlternateSolutionsValue.toUpperCase().split('\n').map(s => s.trim()).filter((e, idx, lst) => e !== '' && lst.indexOf(e) === idx).join('\n');

                // If the user left the default text, alts, and allowAnySolution, don't update them - maybe they only want to update the background
                if (text || alts || allowAnySolution)

                // Convert to the actual rebus data format
                if (text === '' || /^[A-Z]$/.test(text)) {
                  updateCharGridAtLocs([cursorLoc], text);
                } else if (/^[A-Za-z]+$/.test(text)) {
                  // Just alphabetical, so it's a "fillable rebus"
                  updateCharGridAtLocs([cursorLoc], 'r:' + text);
                } else {
                  // An "open rebus" that may be any string (no newlines though)
                  updateCharGridAtLocs([cursorLoc], 'o:' + text);
                }

                // Update the alternate solutions
                if (allowAnySolution) {
                  updateAlternateSolutions('$ANY_SOLUTION$', cursorLoc);
                } else if (alts === '') {
                  // Remove alternate solutions
                  removeAlternateSolutions(cursorLoc);
                } else {
                  // Update alternate solutions
                  updateAlternateSolutions(alts, cursorLoc);
                }
              }
              setRebusModalShowing(false);
            }}
          >Save cell contents</Button>
        </div>

        <hr />

        <div className='mt-4 d-flex'>
          Enter an emoji or character used as the background of this cell (does not impact gameplay):
          <FormControl style={{width: '4rem'}} value={currentBkgdChar} onChange={e => {
            const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
            const graphemes = segmenter.segment(e.target.value);
            setCurrentBkgdChar([...graphemes].pop()?.segment || '');
          }} />
        </div>

        <div className='d-flex justify-content-center mt-3'>
          <Button
            variant='outline-primary'
            onClick={() => {
              if (cursorLoc) {
                updateBackgroundFurnishing(currentBkgdChar, cursorLoc);
              }
              setRebusModalShowing(false);
            }}
          >Save cell background</Button>
        </div>

      </Modal.Body>
    </Modal>
  );


  const currentClueIsHidden = clues.get(currentSlotName)?.includes('$hide$');
  const headerRow = tabKey === TAB_KEY.FILL ? (
    <div className='d-flex mb-2'>

      <div
        id='autofill-toolbar'
        className='d-flex flex-nowrap me-2'
        onMouseEnter={() => setIsHoveringOverAutofillToolbar(true)}
        onMouseLeave={() => setIsHoveringOverAutofillToolbar(false)}
        style={{ width: '165px' }}
      >

        {!continuouslyAutofill ? (
          <Button
            className='w-100'
            size='sm'
            variant='outline-success'
            onClick={() => {
              turnOnContinuousAutofill();
              setIsHoveringOverAutofillToolbar(false);
            }}
          >
            <MdOutlineAutoFixHigh className='me-1' />
            Turn on autofiller
          </Button>
        ) : (
          isHoveringOverAutofillToolbar ? (
            <Button
              className='w-100'
              size='sm'
              variant='info'
              onClick={() => turnOffContinuousAutofill()}
            >Turn off autofiller</Button>
          ) : (
            <>
            
              {!autofillStatus ? <BiMinus className='my-auto ms-1' /> : 
                autofillStatus === AUTOFILL_STATUS.SEARCHING ? <BiLoaderAlt className='spinning my-auto ms-1' /> :
                autofillStatus === AUTOFILL_STATUS.SUCCEEDED ? <BiCheck className='my-auto ms-1 text-success' /> :
                <BiX className='my-auto ms-1 text-danger' /> }


              {wordsMap ? (
                <div className='mx-2 my-auto text-center small' style={{ width: '130px' }}>
                  <div className='text-nowrap'>
                    <MdOutlineAutoFixHigh className='text-muted me-1' />&#183;
                    <span className='text-success mx-1'>{numWordsWithFill}</span>&#183;
                    <span className='text-danger mx-1'>{numWordsWithoutFill}</span>&#183;
                    <span className='text-secondary ms-1'>{numWordsNotSure}</span>
                  </div>
                  <ProgressBar style={{height: '5px'}}>
                    <ProgressBar
                      className='m-0'
                      variant='success'
                      striped={isCurrentlyEvaluatingMore}
                      animated={isCurrentlyEvaluatingMore}
                      now={numWordsWithFill ? numWordsWithFill / totalConsideredWords * 100 : 0}
                      key={1}
                      style={{transition: 'none'}}
                    />
                    <ProgressBar
                      variant='danger'
                      striped={isCurrentlyEvaluatingMore}
                      animated={isCurrentlyEvaluatingMore}
                      now={numWordsWithoutFill ? numWordsWithoutFill / totalConsideredWords * 100 : 0}
                      key={2}
                      style={{transition: 'none'}}
                    />
                  </ProgressBar>
                </div>
              ) : (
                <span className='small my-auto ms-1 text-secondary text-nowrap fst-italic'>Fill assistance</span>
              )}
            </>
          )
        )}
      </div>

      <OverlayTrigger
        trigger='click'
        rootClose
        placement='bottom'
        overlay={<Popover>
          <MinScoreSlider className='m-2' currentMinScore={getPuzzlePreference(PuzzlePreferenceKey.MINIMUM_WORD_SCORE)} setMinScore={setMinScore} />
        </Popover>}
      >
        <Button
          variant='outline-secondary'
          size='sm'
          style={{ borderColor: 'darkgrey' }}
        >{windowWidth > 992 && '≥ '}{getPuzzlePreference(PuzzlePreferenceKey.MINIMUM_WORD_SCORE)}</Button>
      </OverlayTrigger>
      



      <OverlayTrigger
        trigger='click'
        rootClose
        placement='bottom'
        overlay={<Popover>
          <SketchPicker
            color={currentColor}
            onChange={newColor => {
              setCurrentColor(newColor.hex);
              updateFurnishingColorForCursorLoc(newColor.hex);
            }}
            disableAlpha
            presetColors={DEFAULT_COLORS
              .concat(furnishingsObjectToList(furnishings).filter(f => f.furnishingType === FurnishingType.COLOR).map(f => f.value.toLowerCase())) // always show default colors first
              .filter((val, idx, arr) => arr.indexOf(val) === idx)    // unique colors only
            }
          />
        </Popover>}
      >
        <Button
          className='d-flex ms-2'
          onClick={() => {
            updateFurnishingColorForCursorLoc(currentColor);
          }}
          style={{ color: 'black', backgroundColor: currentColor, borderColor: 'darkgrey' }}
        >
          <AiOutlineBgColors className='my-auto' />
        </Button>
      </OverlayTrigger>


      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement='bottom'
        overlay={<Tooltip>{furnishingValueAt(cursorLoc, FurnishingType.SHAPE) ? 'Remove' : 'Add a'} circle</Tooltip>}
      >
        <Button
          className='d-flex ms-2 p-1'
          variant='outline-secondary'
          disabled={!cursorLoc}
          onClick={() => {
            toggleCircleFurnishingsAtLocs([cursorLoc], !furnishingValueAt(cursorLoc, FurnishingType.SHAPE))
          }}
        >
          {furnishingValueAt(cursorLoc, FurnishingType.SHAPE) ? <TbCircleDotted className='my-auto' /> : <FaRegCircle className='my-auto' />}
        </Button>
      </OverlayTrigger>


      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement='bottom'
        overlay={<Tooltip>Add a rebus, symbol, emoji, or background character</Tooltip>}
      >
        <Button
          className='ms-2 py-0 px-1'
          variant='outline-secondary'
          disabled={!cursorLoc}
          onClick={() => setRebusModalShowing(true)}
        >
          <div className='px-1' style={{ fontSize: '13px', lineHeight: '12px' }}>
            &#128516;&#35;<br />
            REB
          </div>
        </Button>
      </OverlayTrigger>



      <div className='d-flex ms-auto'>
        <Button
          className='zoomable'
          variant='success'
          size='sm'
          onClick={() => setTabKey(TAB_KEY.CLUES)}
        >{windowWidth >= 992 && 'Show '}Clues</Button>
      </div>
    </div>
  ) : (
    <div className='d-flex gap-2 mb-2'>
      <OverlayTrigger placement='bottom' overlay={<Tooltip>Highlight other words while solving this clue</Tooltip>}>
        <Button
          className='zoomable'
          disabled={!currentSlotName}
          onClick={() => setClueLinkageModalShowing(true)}
          size='sm'
          variant='info'
        >Link clues</Button>
      </OverlayTrigger>

      <OverlayTrigger placement='bottom' overlay={<Tooltip>{currentClueIsHidden ? 'This clue is hidden from the puzzle' : 'Remove this clue from the puzzle'}</Tooltip>}>
        <Button
          className='zoomable'
          disabled={!currentSlotName}
          onClick={() => {
            if (currentClueIsHidden) {
              // Unhide clue
              setCluesAt([{
                slotName: currentSlotName,
                clueText: removeClueCommand(clues.get(currentSlotName), 'hide'),
              }]);
            } else {
              // Hide clue
              setCluesAt([{
                slotName: currentSlotName,
                clueText: clues.get(currentSlotName) + '$hide$',
              }]);
            }
          }}
          size='sm'
          variant={currentClueIsHidden ? 'secondary' : 'outline-secondary'}
        >{currentClueIsHidden ? 'Unhide' : 'Hide clue'}</Button>
      </OverlayTrigger>

      <Button
        className='ms-auto zoomable'
        variant='success'
        size='sm'
        onClick={() => setTabKey(TAB_KEY.FILL)}
      >{windowWidth >= 992 && 'Show '}Fill Suggestions</Button>
    </div>
  );


  return (
    <div className='ms-3' ref={ref} {...props}>

      {headerRow}

      {tabKey === TAB_KEY.FILL ? <WordSuggestorPanel pxHeight={pxHeight} /> : <CluesPanel pxHeight={pxHeight} triggerClueLinkageModal={() => setClueLinkageModalShowing(true)} />}

      {rebusModal}
      <ClueLinkageModal show={clueLinkageModalShowing} onHide={() => setClueLinkageModalShowing(false)} />

      <KeyboardEventHandler
        handleKeys={[
          'ctrl+shift+f', 'meta+shift+f',
        ]}
        onKeyEvent={(key, event) => {
          event.preventDefault();
          updateFurnishingColorForCursorLoc(currentColor);
        }}
      />
    </div>
  );


});

export default ConstructorSidePanel;